import type {
  CreateMcpCredentialMutationVariables,
  CreateUserMcpCredentialMutationVariables,
  McpCredentialsForUserQuery,
  McpCredentialsQuery,
  UpdateMcpCredentialWorkspaceGrantsForUserMutationVariables,
} from '@affine/graphql';
import { LiveData, Service } from '@toeverything/infra';

import type { McpCredentialStore } from '../stores/mcp-credential';

export type McpCredential = McpCredentialsQuery['mcpCredentials'][number];
export type UserMcpCredential =
  McpCredentialsForUserQuery['mcpCredentialsForUser'][number];

export class McpCredentialService extends Service {
  private revalidationId = 0;

  constructor(private readonly store: McpCredentialStore) {
    super();
  }

  credentials$ = new LiveData<McpCredential[] | null>(null);
  readWriteAvailable$ = new LiveData(false);
  loading$ = new LiveData(false);
  error$ = new LiveData<unknown>(null);

  userCredentials$ = new LiveData<UserMcpCredential[] | null>(null);
  userLoading$ = new LiveData(false);
  userError$ = new LiveData<unknown>(null);

  async revalidate(workspaceId: string) {
    const revalidationId = ++this.revalidationId;
    this.loading$.value = true;
    try {
      const result = await this.store.list(workspaceId);
      if (revalidationId !== this.revalidationId) return;
      this.credentials$.value = result.mcpCredentials;
      this.readWriteAvailable$.value = result.mcpCredentialReadWriteAvailable;
      this.error$.value = null;
    } catch (error) {
      if (revalidationId !== this.revalidationId) return;
      this.error$.value = error;
    } finally {
      if (revalidationId === this.revalidationId) {
        this.loading$.value = false;
      }
    }
  }

  async create(input: CreateMcpCredentialMutationVariables['input']) {
    const revealed = await this.store.create(input);
    await this.revalidate(input.workspaceId);
    return revealed;
  }

  async revalidateForUser(signal?: AbortSignal) {
    this.userLoading$.value = true;
    try {
      const result = await this.store.listForUser(signal);
      this.userCredentials$.value = result.mcpCredentialsForUser;
      this.userError$.value = null;
      return result.mcpCredentialsForUser;
    } catch (error) {
      this.userError$.value = error;
      throw error;
    } finally {
      this.userLoading$.value = false;
    }
  }

  async createForUser(
    input: CreateUserMcpCredentialMutationVariables['input']
  ) {
    const revealed = await this.store.createForUser(input);
    await this.revalidateForUser();
    return revealed;
  }

  async rotate(id: string, workspaceId: string, expirationDays: number) {
    const revealed = await this.store.rotate(id, workspaceId, expirationDays);
    await this.revalidate(workspaceId);
    return revealed;
  }

  async rotateForUser(id: string, expirationDays: number) {
    const revealed = await this.store.rotateForUser(id, expirationDays);
    await this.revalidateForUser();
    return revealed;
  }

  async revealForUser(id: string) {
    return await this.store.revealForUser(id);
  }

  async revoke(id: string, workspaceId: string) {
    await this.store.revoke(id, workspaceId);
    await this.revalidate(workspaceId);
  }

  async revokeForUser(id: string) {
    await this.store.revokeForUser(id);
    await this.revalidateForUser();
  }

  async deleteForUser(id: string) {
    await this.store.deleteForUser(id);
    await this.revalidateForUser();
  }

  async updateWorkspaceGrants(
    id: string,
    workspaceId: string,
    workspaceGrants: CreateMcpCredentialMutationVariables['input']['workspaceGrants']
  ) {
    const updated = await this.store.updateWorkspaceGrants(
      id,
      workspaceId,
      workspaceGrants
    );
    await this.revalidate(workspaceId);
    return updated;
  }

  async updateWorkspaceGrantsForUser(
    id: string,
    workspaceGrants: UpdateMcpCredentialWorkspaceGrantsForUserMutationVariables['workspaceGrants']
  ) {
    const updated = await this.store.updateWorkspaceGrantsForUser(
      id,
      workspaceGrants
    );
    await this.revalidateForUser();
    return updated;
  }
}
